import { useEffect, useState } from "react";
import { Gauge, CheckCircle2, AlertTriangle, ChevronDown } from "lucide-react";
import api from "../../lib/api";

/* Travel Readiness score — categorized transparent checks with "N items need attention". */
export default function ReadinessPanel({ trip }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get(`/trips/${trip.id}/readiness`).then((r) => setData(r.data)).catch(() => {});
  }, [trip.id, trip.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const color = data.score >= 80 ? "emerald" : data.score >= 50 ? "amber" : "rose";
  return (
    <section className="mb-10" data-testid="readiness-panel">
      <button onClick={() => setOpen((v) => !v)} data-testid="readiness-toggle"
        className="w-full border border-zinc-300 bg-white p-4 flex items-center gap-3 text-left hover:border-zinc-500 transition-colors">
        <Gauge className={`w-6 h-6 text-${color}-600 shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-900 text-lg">
            Travel Readiness: <span className={`text-${color}-600`} data-testid="readiness-score">{data.score}%</span>
          </div>
          <div className="text-xs text-muted-foreground" data-testid="readiness-attention">
            {data.attention === 0 ? "Everything checks out — you're ready to go"
              : `${data.attention} item${data.attention > 1 ? "s" : ""} need${data.attention === 1 ? "s" : ""} attention`}
          </div>
        </div>
        <div className="w-28 h-2 bg-zinc-100 shrink-0 hidden sm:block">
          <div className={`h-full bg-${color}-500`} style={{ width: `${data.score}%` }} />
        </div>
        <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border border-t-0 border-zinc-300 bg-white p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.categories.map((c) => (
            <div key={c.key} data-testid={`readiness-cat-${c.key}`}>
              <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{c.title}</div>
              <ul className="space-y-1.5">
                {c.items.map((i, j) => (
                  <li key={j} className="flex items-start gap-1.5 text-xs">
                    {i.done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-px shrink-0" />
                      : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-px shrink-0" />}
                    <span className={i.done ? "text-zinc-700" : "text-zinc-900 font-medium"}>{i.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="sm:col-span-2 lg:col-span-4 text-[11px] text-muted-foreground">
            Scoring is transparent: each check counts equally. It reflects your own preparation —
            it does not imply approval by any government or airline.
          </p>
        </div>
      )}
    </section>
  );
}
